import { ALERT_KIND_LABELS, type AlertSeverity, type TitleKind } from '@ldembalagens/types';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  BarChart3,
  Boxes,
  CalendarDays,
  Package,
  ShoppingCart,
  TriangleAlert,
  Truck,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { EmptyState } from '@/components/common/empty-state';
import { ErrorState } from '@/components/common/error-state';
import { PageHeader } from '@/components/common/page-header';
import { Panel } from '@/components/common/panel';
import { MetricStrip, StatCard } from '@/components/common/stat-card';
import { FormField } from '@/components/forms/form-field';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { dashboardApi, dashboardKeys } from '@/features/dashboard/dashboard-api';
import { documentsApi, documentsKeys } from '@/features/documents/documents-api';
import { financialApi, financialKeys, type TitleQuery } from '@/features/financial/financial-api';
import { periodLabel } from '@/features/financial/financial-utils';
import { reportsApi, reportsKeys } from '@/features/reports/reports-api';
import { useAuth, usePermissions } from '@/hooks/use-auth';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { formatDate, formatDateTime } from '@/lib/format';
import { formatMoney, formatPercent, formatQty } from '@/lib/numbers';
import { cn } from '@/lib/utils';

type Period = { from: string; to: string };
type Preset = 'month' | 'last-month' | '90d' | 'year' | 'custom';

const iso = (d: Date) => d.toISOString().slice(0, 10);

/** Períodos rápidos em UTC (mesma convenção dos relatórios). */
function presetRange(preset: Exclude<Preset, 'custom'>): Period {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  switch (preset) {
    case 'month':
      return { from: iso(new Date(Date.UTC(y, m, 1))), to: iso(now) };
    case 'last-month':
      return { from: iso(new Date(Date.UTC(y, m - 1, 1))), to: iso(new Date(Date.UTC(y, m, 0))) };
    case '90d':
      return { from: iso(new Date(Date.UTC(y, m, now.getUTCDate() - 89))), to: iso(now) };
    case 'year':
      return { from: iso(new Date(Date.UTC(y, 0, 1))), to: iso(now) };
  }
}

const PRESETS: { value: Exclude<Preset, 'custom'>; label: string }[] = [
  { value: 'month', label: 'Este mês' },
  { value: 'last-month', label: 'Mês anterior' },
  { value: '90d', label: '90 dias' },
  { value: 'year', label: 'Ano' },
];

const compactMoney = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  notation: 'compact',
  maximumFractionDigits: 1,
});

const SEVERITY_DOT: Record<AlertSeverity, string> = {
  critical: 'bg-destructive',
  warning: 'bg-warning',
  info: 'bg-info',
};
const SEVERITY_RANK: Record<AlertSeverity, number> = { critical: 2, warning: 1, info: 0 };

/** Dashboard executivo: indicadores do período e posição atual, respeitando permissões. */
export function DashboardPage() {
  useDocumentTitle('Dashboard');
  const { tenant, user } = useAuth();
  const { can } = usePermissions();
  const [preset, setPreset] = useState<Preset>('month');
  const [period, setPeriod] = useState<Period>(presetRange('month'));
  const canFinancial = can('financial:read');

  const overview = useQuery({ queryKey: dashboardKeys.overview, queryFn: dashboardApi.overview });
  const consolidated = useQuery({
    queryKey: reportsKeys.consolidated(period),
    queryFn: () => reportsApi.consolidated(period),
    enabled: Boolean(period.from && period.to),
  });
  const alerts = useQuery({
    queryKey: documentsKeys.alerts,
    queryFn: documentsApi.alerts,
    staleTime: 60 * 1000,
  });
  const data = overview.data;
  const c = consolidated.data;
  const loading = consolidated.isLoading;
  const f = c?.financial;

  const choosePreset = (p: Exclude<Preset, 'custom'>) => {
    setPreset(p);
    setPeriod(presetRange(p));
  };

  return (
    <>
      <PageHeader
        title={`Olá, ${user?.name.split(' ')[0] ?? ''}`}
        description={
          <>
            {tenant ? `Visão geral de ${tenant.tradeName}` : 'Visão geral'} ·{' '}
            <span className="text-foreground">
              {formatDate(period.from)} a {formatDate(period.to)}
            </span>
          </>
        }
        actions={
          <>
            <div className="scrollbar-none -mx-4 flex max-w-[100vw] items-center gap-2 overflow-x-auto px-4 md:mx-0 md:px-0">
              <Tabs
                value={preset}
                onValueChange={(v) => v !== 'custom' && choosePreset(v as never)}
              >
                <TabsList aria-label="Período">
                  {PRESETS.map((p) => (
                    <TabsTrigger key={p.value} value={p.value} className="px-3">
                      {p.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
              <CustomPeriod
                period={period}
                active={preset === 'custom'}
                onApply={(p) => {
                  setPreset('custom');
                  setPeriod(p);
                }}
              />
            </div>
            {can('reports:read') ? (
              <Button asChild variant="outline" className="max-md:hidden">
                <Link to="/relatorios">
                  <BarChart3 /> Relatórios
                </Link>
              </Button>
            ) : null}
          </>
        }
      />

      {consolidated.error ? (
        <ErrorState error={consolidated.error} onRetry={() => void consolidated.refetch()} />
      ) : null}

      {f || (loading && canFinancial) ? (
        <MetricStrip>
          <StatCard
            label="Faturamento"
            tag="Período"
            value={f ? formatMoney(f.revenue) : undefined}
            hint={
              f ? (
                <>
                  Resultado{' '}
                  <span className={Number(f.result) < 0 ? 'text-destructive' : 'text-foreground'}>
                    {formatMoney(f.result)}
                  </span>
                </>
              ) : undefined
            }
            loading={loading}
            to="/financeiro"
          />
          <StatCard
            label="Saldo em caixa e bancos"
            tag="Atual"
            value={f ? formatMoney(f.cashBalance) : undefined}
            tone={f && Number(f.cashBalance) < 0 ? 'negative' : 'default'}
            hint="Contas ativas"
            loading={loading}
            to="/financeiro/contas"
          />
          <StatCard
            label="Contas a pagar"
            tag="Atual"
            value={f ? formatMoney(f.payablesOpen) : undefined}
            hint={
              f ? (
                Number(f.payablesOverdue) > 0 ? (
                  <span className="text-destructive">
                    {formatMoney(f.payablesOverdue)} vencidos
                  </span>
                ) : (
                  'Nada vencido'
                )
              ) : undefined
            }
            loading={loading}
            to="/financeiro/contas-pagar"
          />
          <StatCard
            label="Contas a receber"
            tag="Atual"
            value={f ? formatMoney(f.receivablesOpen) : undefined}
            hint={
              f ? (
                Number(f.receivablesOverdue) > 0 ? (
                  <span className="text-warning">
                    {formatMoney(f.receivablesOverdue)} em atraso
                  </span>
                ) : (
                  'Nada em atraso'
                )
              ) : undefined
            }
            loading={loading}
            to="/financeiro/contas-receber"
          />
        </MetricStrip>
      ) : null}

      <AlertsStrip
        loading={alerts.isLoading}
        total={alerts.data?.total ?? 0}
        items={alerts.data?.items ?? []}
        byKind={alerts.data?.byKind ?? []}
      />

      {f || c?.profitability || c?.production ? (
        <div className="grid gap-5 lg:grid-cols-3">
          {f ? (
            <Panel
              className="lg:col-span-2"
              title="Receitas × despesas"
              description="Por competência, últimos 6 meses"
              actions={<ChartLegend />}
            >
              <FinancialChart monthly={f.monthly} />
            </Panel>
          ) : null}
          <Panel
            title="Resumo do período"
            description={`${formatDate(period.from)} a ${formatDate(period.to)}`}
            className={cn(!f && 'lg:col-span-3')}
            flush
          >
            <dl className="divide-y text-sm">
              {f ? (
                <>
                  <SummaryRow label="Faturamento (competência)" value={formatMoney(f.revenue)} />
                  <SummaryRow label="Recebido" value={formatMoney(f.received)} />
                  <SummaryRow label="Pago" value={formatMoney(f.paid)} />
                  <SummaryRow
                    label="Resultado"
                    value={formatMoney(f.result)}
                    tone={Number(f.result) < 0 ? 'negative' : 'strong'}
                  />
                </>
              ) : null}
              {c?.profitability ? (
                <SummaryRow
                  label="Margem bruta estimada"
                  value={formatMoney(c.profitability.grossMargin)}
                  hint={`${formatPercent(c.profitability.grossMarginPercent)} sobre ${formatMoney(c.profitability.revenue)} em pedidos`}
                  tone={Number(c.profitability.grossMargin) < 0 ? 'negative' : 'default'}
                />
              ) : null}
              {c?.production ? (
                <>
                  <SummaryRow
                    label="Produzido no período"
                    value={formatQty(c.production.producedPeriod)}
                    hint={`${formatQty(c.production.producedToday)} hoje`}
                  />
                  <SummaryRow
                    label="Perdas"
                    value={formatPercent(c.production.lossPercent)}
                    hint="Rejeitado sobre produzido no mês"
                    tone={Number(c.production.lossPercent) > 5 ? 'warning' : 'default'}
                  />
                </>
              ) : null}
            </dl>
          </Panel>
        </div>
      ) : loading ? (
        <Skeleton className="h-72 w-full rounded-xl" />
      ) : null}

      <div className="grid gap-5 lg:grid-cols-3">
        {canFinancial ? <UpcomingDues /> : null}
        {c?.operations ? (
          <Panel title="Operação" description="Posição atual" flush>
            <ul className="divide-y">
              {can('stock:read') ? (
                <OperationRow
                  icon={Boxes}
                  label="Estoque disponível"
                  value={formatMoney(c.operations.stockValue)}
                  hint={`${c.operations.stockAvailableItems} itens`}
                  alert={
                    c.operations.belowMinimum > 0
                      ? `${c.operations.belowMinimum} abaixo do mínimo`
                      : undefined
                  }
                  to="/estoque"
                />
              ) : null}
              {can('sales:read') ? (
                <OperationRow
                  icon={ShoppingCart}
                  label="Pedidos pendentes"
                  value={c.operations.pendingSalesOrders}
                  hint={`${c.operations.openQuotations} orçamentos em aberto`}
                  to="/vendas/pedidos"
                />
              ) : null}
              {can('purchases:read') ? (
                <OperationRow
                  icon={Package}
                  label="Compras em aberto"
                  value={c.operations.openPurchaseOrders}
                  to="/compras/pedidos"
                />
              ) : null}
              {can('maintenance:read') || can('assets:read') ? (
                <OperationRow
                  icon={Wrench}
                  label="Equipamentos em manutenção"
                  value={c.operations.assetsInMaintenance}
                  hint={`${c.operations.scheduledMaintenance} programadas`}
                  alert={
                    c.operations.lateMaintenance > 0
                      ? `${c.operations.lateMaintenance} atrasada(s)`
                      : undefined
                  }
                  to="/manutencoes/ordens"
                />
              ) : null}
              {can('logistics:read') ? (
                <OperationRow
                  icon={Truck}
                  label="Entregas pendentes"
                  value={c.operations.pendingDeliveries}
                  alert={
                    c.operations.lateDeliveries > 0
                      ? `${c.operations.lateDeliveries} atrasada(s)`
                      : undefined
                  }
                  to="/logistica/entregas"
                />
              ) : null}
            </ul>
          </Panel>
        ) : null}
        {c?.production ? (
          <Panel
            title="Produção"
            description="Ordens e volume por produto"
            actions={
              <Button asChild variant="ghost" size="sm">
                <Link to="/producao/ordens">
                  Ordens <ArrowRight />
                </Link>
              </Button>
            }
            flush
          >
            <div className="grid grid-cols-3 divide-x border-b text-center">
              <MiniStat label="Abertas" value={c.production.openOrders} />
              <MiniStat label="Em andamento" value={c.production.inProgressOrders} />
              <MiniStat
                label="Atrasadas"
                value={c.production.lateOrders}
                tone={c.production.lateOrders > 0 ? 'negative' : 'default'}
              />
            </div>
            {c.production.byProduct.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                Sem produção no período.
              </p>
            ) : (
              <ul className="divide-y text-sm">
                {c.production.byProduct.slice(0, 5).map((p) => (
                  <li key={p.productId} className="flex justify-between gap-3 px-4 py-2.5">
                    <span className="truncate">{p.productName}</span>
                    <span className="font-medium tabular-nums">{formatQty(p.produced)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        ) : null}
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        {c?.profitability && c.profitability.topProducts.length > 0 ? (
          <Panel
            className="lg:col-span-2"
            title="Rentabilidade por produto"
            description="Receita de pedidos no período e margem sobre o custo médio em estoque"
            actions={
              can('reports:read') ? (
                <Button asChild variant="ghost" size="sm">
                  <Link to="/relatorios/sales-by-product">
                    Relatório <ArrowRight />
                  </Link>
                </Button>
              ) : null
            }
            flush
          >
            <ul className="divide-y text-sm">
              {c.profitability.topProducts.map((p) => (
                <li
                  key={p.productId}
                  className="flex items-center justify-between gap-3 px-4 py-2.5"
                >
                  <span className="min-w-0 truncate">{p.productName}</span>
                  <span className="flex shrink-0 items-center gap-3 tabular-nums">
                    <span>{formatMoney(p.revenue)}</span>
                    <Badge
                      className="min-w-14"
                      variant={
                        Number(p.marginPercent) < 0
                          ? 'destructive'
                          : Number(p.marginPercent) < 15
                            ? 'warning'
                            : 'success'
                      }
                    >
                      {formatPercent(p.marginPercent)}
                    </Badge>
                  </span>
                </li>
              ))}
            </ul>
          </Panel>
        ) : null}
        <Panel
          title="Cadastros e acessos"
          className={cn(
            !(c?.profitability && c.profitability.topProducts.length > 0) && 'lg:col-span-3',
          )}
          flush
        >
          {overview.error ? (
            <div className="p-4">
              <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />
            </div>
          ) : (
            <dl className="grid grid-cols-2 gap-px bg-border text-sm md:grid-cols-4 [&>div]:bg-card">
              <MiniStat label="Clientes ativos" value={data?.registrations.customers} />
              <MiniStat label="Fornecedores ativos" value={data?.registrations.suppliers} />
              <MiniStat
                label="Logins (7 dias)"
                value={data?.activity.loginsLast7Days}
                hint={data ? `${data.tenant.usersActive} usuários ativos` : undefined}
              />
              <MiniStat
                label="Falhas de login (7 dias)"
                value={data?.activity.failedLoginsLast7Days}
                tone={(data?.activity.failedLoginsLast7Days ?? 0) > 10 ? 'warning' : 'default'}
              />
            </dl>
          )}
        </Panel>
      </div>

      {data ? (
        <p className="text-xs text-muted-foreground">
          {data.modules.every((m) => m.available)
            ? `Todos os ${data.modules.length} módulos disponíveis`
            : `Em desenvolvimento: ${data.modules
                .filter((m) => !m.available)
                .map((m) => `${m.label} (fase ${m.phase})`)
                .join(', ')}`}{' '}
          · atualizado em {formatDateTime(data.generatedAt)}
        </p>
      ) : null}
    </>
  );
}

function CustomPeriod({
  period,
  active,
  onApply,
}: {
  period: Period;
  active: boolean;
  onApply: (p: Period) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(period);
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) setDraft(period);
      }}
    >
      <PopoverTrigger asChild>
        <Button variant={active ? 'soft' : 'outline'} className="shrink-0">
          <CalendarDays /> {active ? 'Personalizado' : 'Período'}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <FormField id="d-from" label="De">
            <Input
              id="d-from"
              type="date"
              value={draft.from}
              onChange={(e) => setDraft((p) => ({ ...p, from: e.target.value }))}
            />
          </FormField>
          <FormField id="d-to" label="Até">
            <Input
              id="d-to"
              type="date"
              value={draft.to}
              onChange={(e) => setDraft((p) => ({ ...p, to: e.target.value }))}
            />
          </FormField>
        </div>
        <Button
          className="w-full"
          disabled={!draft.from || !draft.to || draft.from > draft.to}
          onClick={() => {
            onApply(draft);
            setOpen(false);
          }}
        >
          Aplicar período
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function AlertsStrip({
  loading,
  total,
  items,
  byKind,
}: {
  loading: boolean;
  total: number;
  items: { kind: keyof typeof ALERT_KIND_LABELS; severity: AlertSeverity }[];
  byKind: { kind: keyof typeof ALERT_KIND_LABELS; count: number }[];
}) {
  if (loading) return <Skeleton className="h-12 w-full rounded-xl" />;
  if (total === 0) return null;
  const severityOf = (kind: string): AlertSeverity =>
    items
      .filter((i) => i.kind === kind)
      .reduce<AlertSeverity>(
        (max, i) => (SEVERITY_RANK[i.severity] > SEVERITY_RANK[max] ? i.severity : max),
        'info',
      );
  const kinds = [...byKind].sort(
    (a, b) => SEVERITY_RANK[severityOf(b.kind)] - SEVERITY_RANK[severityOf(a.kind)],
  );
  return (
    <section
      aria-label="Alertas"
      className="flex flex-col gap-2 rounded-xl border bg-card px-3 py-2.5 sm:flex-row sm:items-center sm:gap-3"
    >
      <p className="flex shrink-0 items-center gap-2 px-1 text-sm font-medium">
        <TriangleAlert className="size-4 text-warning" aria-hidden />
        {total} {total === 1 ? 'alerta' : 'alertas'}
      </p>
      <ul className="scrollbar-none -mx-3 flex min-w-0 flex-1 gap-1.5 overflow-x-auto px-3 sm:mx-0 sm:flex-wrap sm:px-0">
        {kinds.map((k) => (
          <li key={k.kind} className="shrink-0">
            <Link
              to={`/alertas?tipo=${k.kind}`}
              className="inline-flex h-8 items-center gap-2 rounded-full border bg-subtle px-3 text-[13px] transition-colors outline-none hover:border-border-strong hover:bg-card focus-visible:ring-[3px] focus-visible:ring-primary/25"
            >
              <span
                className={cn('size-2 rounded-full', SEVERITY_DOT[severityOf(k.kind)])}
                aria-hidden
              />
              {ALERT_KIND_LABELS[k.kind]}
              <span className="font-semibold tabular-nums">{k.count}</span>
            </Link>
          </li>
        ))}
      </ul>
      <Button asChild variant="ghost" size="sm" className="shrink-0 self-end sm:self-auto">
        <Link to="/alertas">
          Ver todos <ArrowRight />
        </Link>
      </Button>
    </section>
  );
}

function ChartLegend() {
  return (
    <ul className="flex items-center gap-3 text-xs text-muted-foreground">
      <li className="flex items-center gap-1.5">
        <span className="size-2.5 rounded-[3px] bg-(--chart-income)" aria-hidden /> Receitas
      </li>
      <li className="flex items-center gap-1.5">
        <span className="size-2.5 rounded-[3px] bg-(--chart-expense)" aria-hidden /> Despesas
      </li>
    </ul>
  );
}

function FinancialChart({
  monthly,
}: {
  monthly: { month: string; income: string; expense: string; result: string }[];
}) {
  const rows = monthly.map((m) => ({
    month: periodLabel(m.month),
    receitas: Number(m.income),
    despesas: Number(m.expense),
    resultado: Number(m.result),
  }));
  if (rows.length === 0)
    return (
      <EmptyState title="Sem movimentação" description="Nenhum lançamento nos últimos meses." />
    );
  return (
    <div className="h-64" role="img" aria-label="Gráfico de receitas e despesas mensais">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} barGap={2} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="var(--chart-grid)" />
          <XAxis
            dataKey="month"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            width={72}
            tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
            tickFormatter={(v: number) => compactMoney.format(v)}
          />
          <Tooltip
            cursor={{ fill: 'var(--muted)', opacity: 0.6 }}
            content={({ active, payload, label }) =>
              active && payload?.length ? (
                <div className="min-w-44 rounded-lg border bg-popover px-3 py-2 text-xs shadow-md">
                  <p className="mb-1 font-medium text-foreground">{label}</p>
                  {(['receitas', 'despesas', 'resultado'] as const).map((key) => {
                    const value = (payload[0]?.payload as Record<string, number>)[key] ?? 0;
                    return (
                      <p key={key} className="flex justify-between gap-4 text-muted-foreground">
                        <span className="capitalize">{key}</span>
                        <span
                          className={cn(
                            'font-medium tabular-nums text-foreground',
                            key === 'resultado' && value < 0 && 'text-destructive',
                          )}
                        >
                          {formatMoney(value)}
                        </span>
                      </p>
                    );
                  })}
                </div>
              ) : null
            }
          />
          <Bar
            dataKey="receitas"
            name="Receitas"
            fill="var(--chart-income)"
            radius={[4, 4, 0, 0]}
            maxBarSize={22}
          />
          <Bar
            dataKey="despesas"
            name="Despesas"
            fill="var(--chart-expense)"
            radius={[4, 4, 0, 0]}
            maxBarSize={22}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function SummaryRow({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: 'default' | 'strong' | 'negative' | 'warning';
}) {
  return (
    <div className="flex items-start justify-between gap-3 px-4 py-2.5">
      <dt className="min-w-0 text-muted-foreground">
        {label}
        {hint ? <span className="block text-xs">{hint}</span> : null}
      </dt>
      <dd
        className={cn(
          'shrink-0 tabular-nums',
          tone === 'strong' && 'font-semibold',
          tone === 'negative' && 'font-semibold text-destructive',
          tone === 'warning' && 'text-warning',
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function MiniStat({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: 'default' | 'negative' | 'warning';
}) {
  return (
    <div className="px-4 py-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          'mt-0.5 text-lg font-semibold tabular-nums',
          tone === 'negative' && 'text-destructive',
          tone === 'warning' && 'text-warning',
        )}
      >
        {value ?? '—'}
      </dd>
      {hint ? <dd className="text-xs text-muted-foreground">{hint}</dd> : null}
    </div>
  );
}

function OperationRow({
  icon: Icon,
  label,
  value,
  hint,
  alert,
  to,
}: {
  icon: LucideIcon;
  label: string;
  value: ReactNode;
  hint?: string;
  alert?: string;
  to: string;
}) {
  return (
    <li>
      <Link
        to={to}
        className="flex items-center gap-3 px-4 py-2.5 transition-colors outline-none hover:bg-subtle focus-visible:bg-subtle"
      >
        <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm">{label}</span>
          {alert || hint ? (
            <span
              className={cn(
                'block truncate text-xs',
                alert ? 'text-warning' : 'text-muted-foreground',
              )}
            >
              {alert ?? hint}
            </span>
          ) : null}
        </span>
        <span className="shrink-0 text-sm font-semibold tabular-nums">{value}</span>
      </Link>
    </li>
  );
}

/** Próximos 30 dias de contas a pagar/receber em aberto (vencidas ficam nos alertas). */
function UpcomingDues() {
  const [kind, setKind] = useState<TitleKind>('PAYABLE');
  const today = iso(new Date());
  const limit = new Date();
  limit.setUTCDate(limit.getUTCDate() + 30);
  const query: TitleQuery = {
    open: true,
    from: today,
    to: iso(limit),
    pageSize: 6,
    sortBy: 'dueDate',
    sortOrder: 'asc',
  };
  const titles = useQuery({
    queryKey: financialKeys.titles(kind, query),
    queryFn: () => financialApi.titles(kind, query),
  });
  const rows = titles.data?.data ?? [];
  const dayDiff = (d: string) =>
    Math.round(
      (new Date(`${d.slice(0, 10)}T00:00:00Z`).getTime() -
        new Date(`${today}T00:00:00Z`).getTime()) /
        86_400_000,
    );

  return (
    <Panel
      title="Próximos vencimentos"
      description="Em aberto, próximos 30 dias"
      actions={
        <Tabs value={kind} onValueChange={(v) => setKind(v as TitleKind)}>
          <TabsList aria-label="Tipo de conta" className="h-8">
            <TabsTrigger value="PAYABLE" className="text-xs">
              A pagar
            </TabsTrigger>
            <TabsTrigger value="RECEIVABLE" className="text-xs">
              A receber
            </TabsTrigger>
          </TabsList>
        </Tabs>
      }
      flush
    >
      {titles.error ? (
        <div className="p-4">
          <ErrorState error={titles.error} onRetry={() => void titles.refetch()} />
        </div>
      ) : titles.isLoading ? (
        <div className="space-y-3 p-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">
          Nenhum vencimento nos próximos 30 dias.
        </p>
      ) : (
        <ul className="divide-y">
          {rows.map((t) => {
            const days = dayDiff(t.dueDate);
            return (
              <li key={t.id} className="flex items-center gap-3 px-4 py-2.5">
                <span
                  className={cn(
                    'flex w-12 shrink-0 flex-col items-center rounded-md border py-1 text-center leading-tight',
                    days <= 3 && 'border-warning/30 bg-warning-soft',
                  )}
                >
                  <span className="text-[13px] font-semibold tabular-nums">
                    {formatDate(t.dueDate).slice(0, 5)}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {days === 0 ? 'hoje' : `${days}d`}
                  </span>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm">{t.description}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {t.partyName ?? t.categoryName}
                    {t.installments > 1 ? ` · ${t.installment}/${t.installments}` : ''}
                  </span>
                </span>
                <span className="shrink-0 text-sm font-medium tabular-nums">
                  {formatMoney(t.openAmount)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <div className="border-t px-2 py-1.5">
        <Button asChild variant="ghost" size="sm" className="w-full justify-center">
          <Link to={kind === 'PAYABLE' ? '/financeiro/contas-pagar' : '/financeiro/contas-receber'}>
            Ver {kind === 'PAYABLE' ? 'contas a pagar' : 'contas a receber'} <ArrowRight />
          </Link>
        </Button>
      </div>
    </Panel>
  );
}
